import React, { useCallback, useContext, useRef, useState } from "react";
import styled from "styled-components";
import TeWhataKuraLogo from "@assets/icons/hnz_twk3.svg";
import SettingsIcon from "@assets/icons/settings-icon.svg";
import { Link } from "react-router-dom";

import Navigation from "./Navigation";
import mediaQuery from "@utils/mediaQuery";
import EditProfile from "./EditProfile";
import useClickOutside from "@hooks/useHandleClickOutside";
import { OnboardingContext } from "@context/onboardingContext";
import ROUTES from "@constants/routes";
import FeedbackForm from "./FeedbackForm";

const Container = styled.div`
  position: relative;
  z-index: 3;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 16px;
  margin: 0 auto;
  box-sizing: border-box;

  ${mediaQuery("tablet")`
    padding: 16px 100px;
    max-width: 1320px;
  `}
`;

const SettingsButton = styled.button`
  display: none;

  ${mediaQuery("desktop")`
    display: flex;
    padding: 12px 16px;
    height: 50px;
    align-items: center;
    gap: 4px;
    border-radius: 8px;
    border: 1px solid var(--border-default);
  `}
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
`;

const TeWhataKuraLogoWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  height: 40px;

  ${mediaQuery("tablet")`
    height: 50px;
  `}

  svg {
    height: 100%;
    width: auto;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }

  &:visited {
    text-decoration: none;
  }

  &:active {
    text-decoration: none;
  }
`;

const SettingsIconWrapper = styled.div`
  svg {
    path {
      fill: var(--interactive-primary);
    }
  }
`;

const NavContainer = styled.div`
  display: flex;
  align-items: center;
`;

const MobileSettingsButton = styled.button`
  padding: 6px;
  cursor: pointer;

  ${mediaQuery("tablet")`
    margin-right: 20px;
  `}

  ${mediaQuery("desktop")`
    display: none;
  `}
`;

const ProfileSection = styled.div`
  position: relative;
`;

const ProfileWrapper = styled.div`
  position: absolute;
  top: 70px;
  right: -8px;

  ${mediaQuery("tablet")`
    top: 30px;
    right: 10px;
  `}

  ${mediaQuery("desktop")`
    top: 70px;
    right: 10px;
  `}
`;

const Logo = () => (
  <StyledLink to={ROUTES.guidelines.to}>
    <LogoContainer>
      <TeWhataKuraLogoWrapper>
        <TeWhataKuraLogo />
      </TeWhataKuraLogoWrapper>
    </LogoContainer>
  </StyledLink>
);

interface SettingsProps {
  onToggle: () => void;
  isOpen: boolean;
  onFeedbackClick: () => void;
}

const MobileSettings = ({
  onToggle,
  isOpen,
  onFeedbackClick,
}: SettingsProps) => {
  const profileMobileRef = useRef<HTMLDivElement>(null);
  useClickOutside(profileMobileRef, () => isOpen && onToggle());

  return (
    <div ref={profileMobileRef}>
      <MobileSettingsButton onClick={onToggle}>
        <SettingsIconWrapper>
          <SettingsIcon />
        </SettingsIconWrapper>
      </MobileSettingsButton>
      <ProfileWrapper>
        {isOpen && (
          <EditProfile onFeedBackClick={onFeedbackClick} onEdit={onToggle} />
        )}
      </ProfileWrapper>
    </div>
  );
};

const DesktopSettings = ({
  onToggle,
  isOpen,
  onFeedbackClick,
}: SettingsProps) => {
  const profileRef = useRef<HTMLDivElement>(null);
  useClickOutside(profileRef, () => isOpen && onToggle());

  return (
    <ProfileSection ref={profileRef}>
      <SettingsButton onClick={onToggle}>
        <span>
          <SettingsIconWrapper>
            <SettingsIcon />
          </SettingsIconWrapper>
        </span>
        Settings
      </SettingsButton>
      <ProfileWrapper>
        {isOpen && (
          <EditProfile onFeedBackClick={onFeedbackClick} onEdit={onToggle} />
        )}
      </ProfileWrapper>
    </ProfileSection>
  );
};

const Header = () => {
  const [showDesktopProfile, setShowDesktopProfile] = useState(false);
  const [showMobileProfile, setShowMobileProfile] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const { hasSeenOnboarding } = useContext(OnboardingContext) || {};

  const handleFeedbackClick = useCallback(() => {
    setShowFeedbackForm(true);
  }, []);

  const toggleMobileProfile = useCallback(() => {
    setShowMobileProfile((prev) => !prev);
  }, []);

  const toggleDesktopProfile = useCallback(() => {
    setShowDesktopProfile((prev) => !prev);
  }, []);

  // Conditionally include Feedback Labeling link in staging mode only
  const isStaging = import.meta.env.VITE_SANITY_PERSPECTIVE === 'previewDrafts';
  const navigationLinks = [
    ROUTES.update,
    ROUTES.expert,
    ROUTES.dosing,
    ROUTES.about,
    ...(isStaging ? [ROUTES.feedbackLabeling] : [])
  ];

  return (
    <Container>
      <Logo />

      <NavContainer>
        {hasSeenOnboarding && (
          <MobileSettings
            onToggle={toggleMobileProfile}
            isOpen={showMobileProfile}
            onFeedbackClick={handleFeedbackClick}
          />
        )}

        <Navigation
          links={navigationLinks}
        />

        {hasSeenOnboarding && (
          <>
            <DesktopSettings
              onToggle={toggleDesktopProfile}
              isOpen={showDesktopProfile}
              onFeedbackClick={handleFeedbackClick}
            />
          </>
        )}
        <FeedbackForm
          visible={showFeedbackForm}
          onVisibleChange={setShowFeedbackForm}
          treatment="Edit Profile"
          recommendationID=""
          recommendationName=""
        />
      </NavContainer>
    </Container>
  );
};

export default Header;